import React, { useState } from 'react';

/**
 * Multi Profile Manager - Manage multiple EHR/SSO profiles
 * @param {array} profiles - Array of profile objects
 * @param {string} profileType - 'ehr' or 'sso'
 * @param {string} distinguishingField - Field name to distinguish profiles (ehr_system_name|sso_provider_type)
 * @param {function} onAdd - Add profile handler
 * @param {function} onEdit - Edit profile handler
 * @param {function} onDelete - Delete profile handler
 * @param {boolean} readOnly - Read-only mode
 */
const MultiProfileManager = ({
  profiles = [],
  profileType = 'ehr',
  distinguishingField = 'ehr_system_name',
  onAdd,
  onEdit,
  onDelete,
  readOnly = false
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  const title = profileType === 'ehr' ? 'EHR Profiles' : 'SSO Configurations';
  const icon = profileType === 'ehr' ? 'fa-database' : 'fa-lock';
  const placeholder = profileType === 'ehr' ? 'Epic, Cerner, Athena, etc.' : 'Microsoft, Google, Okta, etc.';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      onEdit && onEdit(editingId, formData);
      setEditingId(null);
    } else {
      onAdd && onAdd(formData);
    }
    setFormData({});
    setShowAddForm(false);
  };

  const handleEdit = (profile) => {
    setFormData(profile);
    setEditingId(profile.id);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setFormData({});
    setEditingId(null);
    setShowAddForm(false);
  };

  return (
    <div className="card">
      <div className="card-header py-2 bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0"><i className={`fas ${icon} me-2`} />{title}</h6>
        {!readOnly && !showAddForm && (
          <button className="btn btn-sm btn-primary" onClick={() => setShowAddForm(true)}>
            <i className="fas fa-plus me-1" />Add
          </button>
        )}
      </div>
      <div className="card-body p-2">
        {showAddForm && (
          <form onSubmit={handleSubmit} className="border rounded p-2 mb-2 bg-light">
            <div className="mb-2">
              <label className="form-label form-label-sm mb-1">
                {profileType === 'ehr' ? 'EHR System Name' : 'SSO Provider'}
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={placeholder}
                value={formData[distinguishingField] || ''}
                onChange={(e) => setFormData({ ...formData, [distinguishingField]: e.target.value })}
                required
              />
            </div>
            <div className="btn-group btn-group-sm">
              <button type="submit" className="btn btn-success">
                <i className="fas fa-check me-1" />{editingId ? 'Update' : 'Add'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                <i className="fas fa-times me-1" />Cancel
              </button>
            </div>
          </form>
        )}

        {profiles.length === 0 ? (
          <div className="text-center text-muted small py-2">
            <i className={`fas ${icon} me-1`} />
            No {title.toLowerCase()} configured
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {profiles.map((profile, idx) => (
              <div key={profile.id || idx} className="list-group-item p-2 d-flex justify-content-between align-items-center">
                <div>
                  <strong className="text-primary">{profile[distinguishingField] || 'Unnamed'}</strong>
                  {profile.created_at && (
                    <small className="text-muted d-block">Added: {new Date(profile.created_at).toLocaleDateString()}</small>
                  )}
                </div>
                {!readOnly && (
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-primary" onClick={() => handleEdit(profile)}>
                      <i className="fas fa-edit" />
                    </button>
                    <button className="btn btn-outline-danger" onClick={() => onDelete && onDelete(profile.id)}>
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiProfileManager;
